import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import React from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useOrganizationEnvironments } from '@/hooks/useEnvironments';
import { useProjects } from '@/hooks/useProjects';
import type { ApiEnvironment, ApiProject, EnvironmentsResponse, ProjectsResponse } from '@envase/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { CreateEnvironmentModal } from '@/components/environments/CreateEnvironmentModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/hooks/useToast';
import { useDeleteEnvironment } from '@/hooks/useEnvironments';
import { useTranslation } from '@/hooks/useTranslation';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  PlusIcon, 
  SearchIcon, 
  GlobeIcon,
  LockIcon,
  UnlockIcon,
  SettingsIcon,
  EditIcon,
  MoreHorizontalIcon,
  ShieldIcon,
  UsersIcon,
  BuildingIcon,
  HomeIcon
} from 'lucide-react';
import { loadAuthGuard } from '@/lib/auth-helpers';

export const Route = createFileRoute('/_authenticated/org/$orgId/environments')({
  loader: loadAuthGuard<{ orgId: string }>(async ({ params, context, ensureQueryData, sessionReady }) => {
    const { orgId } = params;
    const { trpc } = context;

    if (!sessionReady) {
      return { orgId };
    }
    
    // Preload organizations to get organizationId from slug
    const orgsData = await ensureQueryData(
      trpc.organizations.getAll.queryOptions(undefined),
    );
    
    // Find organization by slug
    const orgs = (orgsData as { organizations?: Array<{ id: string; slug: string }> })?.organizations || [];
    const organization = orgs.find((o) => o.slug === orgId || o.id === orgId);
    const organizationId = organization?.id;
    
    // Preload environments and projects if we have organizationId
    if (organizationId) {
      await ensureQueryData(
        trpc.environments.getByOrganization.queryOptions({ organizationId }),
      );
      await ensureQueryData(
        trpc.projects.getAll.queryOptions(undefined),
      );
    }
    
    return { orgId };
  }),
  component: OrganizationEnvironmentsPage,
});

function OrganizationEnvironmentsPage() {
  const { orgId } = Route.useParams();
  const { t } = useTranslation();
  const { organization } = useOrganization(orgId);
  const toast = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'protected' | 'open'>('all');
  const [secretsFilter, setSecretsFilter] = useState<'all' | 'with' | 'without'>('all');
  const [createEnvironmentOpen, setCreateEnvironmentOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateEnvs, setTemplateEnvs] = useState<string[]>(['development', 'staging', 'production']);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  const { data: environmentsData, isLoading, refetch } = useOrganizationEnvironments(organization?.slug || '');
  const environments: ApiEnvironment[] = (environmentsData as EnvironmentsResponse | undefined)?.environments || [];

  // Projects logic
  const { data: projectsData, isLoading: isLoadingProjects, refetch: refetchProjects } = useProjects();
  const orgProjects: ApiProject[] = ((projectsData as ProjectsResponse | undefined)?.projects || []).filter(
    (project: ApiProject) => project.organizationId === organization?.id
  );

  // Create delete mutation
  const deleteEnvironmentMutation = useDeleteEnvironment('');

  // Handle environment actions
  const handleDeleteEnvironment = async (environmentId: string, environmentName: string, projectId: string) => {
    if (!confirm(`Are you sure you want to delete "${environmentName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Use the delete environment mutation
      await deleteEnvironmentMutation.mutateAsync({ environmentId });
      
      toast.success('Environment deleted successfully');
      refetch();
    } catch (error) {
      console.error('Delete environment error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete environment');
    }
  };

  const handleEditEnvironment = (environment: ApiEnvironment) => {
    // Navigate to the project's environments page for editing
    window.location.href = `/org/${orgId}/projects/${environment.projectId}/environments`;
  };

  const handleViewDetails = (environment: ApiEnvironment) => {
    // Navigate to the project's environments page
    window.location.href = `/org/${orgId}/projects/${environment.projectId}/environments`;
  };

  const handleSettings = (environment: ApiEnvironment) => {
    // Navigate to the project's environments page for settings
    window.location.href = `/org/${orgId}/projects/${environment.projectId}/environments`;
  };

  const projects = useMemo(() => {
    const map = new Map<string, string>();
    environments.forEach((e: ApiEnvironment) => {
      if (e.projectId && e.projectName) map.set(e.projectId, e.projectName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [environments]);

  const filteredEnvironments = useMemo(() => {
    return environments.filter((env: ApiEnvironment) => {
      const matchesQuery = searchQuery
        ? (env.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           env.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           env.projectName?.toLowerCase().includes(searchQuery.toLowerCase()))
        : true;

      const matchesProject = selectedProjectId === 'all' ? true : env.projectId === selectedProjectId;

      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'protected'
          ? Boolean(env.protected)
          : !Boolean(env.protected);

      const matchesSecrets =
        secretsFilter === 'all'
          ? true
          : secretsFilter === 'with'
          ? (env.secretCount || 0) > 0
          : (env.secretCount || 0) === 0;

      return matchesQuery && matchesProject && matchesStatus && matchesSecrets;
    });
  }, [environments, searchQuery, selectedProjectId, statusFilter, secretsFilter]);

  const totalSecrets = environments.reduce((sum: number, env: ApiEnvironment) => sum + (env.secretCount || 0), 0);
  const protectedCount = environments.filter((e: ApiEnvironment) => e.protected).length;

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Environments',
      to: `/org/${organization?.slug || orgId}/environments`,
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={t('orgEnvironments.title')}
          action={
            <Button
              asChild
              className="bg-primary text-white hover:bg-primary/90 py-2 px-4 rounded"
            >
              <Link
                to="/dashboard"
                tabIndex={0}
              >
                <BuildingIcon className="w-5 h-5 mr-2" />
                {t('orgEnvironments.selectOrg')}
              </Link>
            </Button>
          }
        />
        <div className="flex items-center justify-center h-64">
          <Loader variant="spinner" size="lg" color="accent" text="Loading environments..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={t('orgEnvironments.title')}
        action={
          <Button
            asChild
            className="bg-primary text-white hover:bg-primary/90 py-2 px-4 rounded"
          >
            <Link
              to="/dashboard"
              tabIndex={0}
            >
              <BuildingIcon className="w-5 h-5 mr-2" />
              {t('orgEnvironments.selectOrg')}
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder={t('orgEnvironments.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-environments-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="environment-filters">
            <div className="space-y-2">
              <Label>{t('orgEnvironments.filters.project')}</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger data-testid="filter-project">
                  <SelectValue placeholder={t('orgEnvironments.filters.project')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('orgEnvironments.filters.statusAll')}</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('orgEnvironments.filters.status')}</Label>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'protected' | 'open')}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('orgEnvironments.filters.statusAll')}</SelectItem>
                  <SelectItem value="protected">{t('orgEnvironments.filters.statusProtected')}</SelectItem>
                  <SelectItem value="open">{t('orgEnvironments.filters.statusOpen')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('orgEnvironments.filters.secrets')}</Label>
              <Select value={secretsFilter} onValueChange={(v) => setSecretsFilter(v as 'all' | 'with' | 'without')}>
                <SelectTrigger data-testid="filter-secrets">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('orgEnvironments.filters.secretsAll')}</SelectItem>
                  <SelectItem value="with">{t('orgEnvironments.filters.secretsWith')}</SelectItem>
                  <SelectItem value="without">{t('orgEnvironments.filters.secretsWithout')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-500">
            <button
              className="underline"
              onClick={() => { setSelectedProjectId('all'); setStatusFilter('all'); setSecretsFilter('all'); setSearchQuery(''); }}
              data-testid="filters-clear"
            >
              {t('orgEnvironments.filters.clear')}
            </button>
            <span data-testid="filters-results">{t('orgEnvironments.filters.results', { count: filteredEnvironments.length })}</span>
          </div>
          <div className="border-t border-gray-200 my-2" />
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t('orgEnvironments.templates.title')}</h3>
            <p className="text-sm text-gray-500">{t('orgEnvironments.templates.subtitle')}</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" data-testid="template-dsp" onClick={() => setTemplateDialogOpen(true)}>
                {t('orgEnvironments.templates.devStagingProd')}
              </Button>
              <Button variant="outline" size="sm" data-testid="template-apply" onClick={() => setTemplateDialogOpen(true)}>
                {t('orgEnvironments.templates.apply')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{t('orgEnvironments.templates.dialog.title')}</DialogTitle>
            <DialogDescription>{t('orgEnvironments.templates.dialog.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2" data-testid="template-projects">
              <div className="text-sm font-medium">{t('orgEnvironments.templates.dialog.selectProjects')}</div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <button className="underline" onClick={() => setSelectedProjectIds(projects.map(p => p.id))}>{t('orgEnvironments.templates.dialog.selectAll')}</button>
                <button className="underline" onClick={() => setSelectedProjectIds([])}>{t('orgEnvironments.templates.dialog.clearAll')}</button>
                <span>· {t('orgEnvironments.templates.dialog.projects')}: {projects.length}</span>
              </div>
              <div className="max-h-40 overflow-auto border rounded p-2 space-y-1" data-testid="template-projects-list">
                {projects.map(p => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedProjectIds.includes(p.id)}
                      onChange={(e) => {
                        setSelectedProjectIds(prev => e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id));
                      }}
                    />
                    <span>{p.name}</span>
                  </label>
                ))}
                {projects.length === 0 && (
                  <div className="text-sm text-gray-500">—</div>
                )}
              </div>
            </div>

            <div className="space-y-2" data-testid="template-envs">
              <div className="text-sm font-medium">{t('orgEnvironments.templates.dialog.environmentsToCreate')}</div>
              {templateEnvs.map((name, idx) => (
                <div key={`${name}-${idx}`} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => {
                      setTemplateEnvs(prev => prev.filter((_, i) => i !== idx));
                    }}
                  />
                  <input
                    className="flex-1 border rounded px-2 py-1 text-sm"
                    value={name}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTemplateEnvs(prev => prev.map((n, i) => (i === idx ? v : n)));
                    }}
                    placeholder={t('orgEnvironments.templates.dialog.name') || 'Nombre'}
                  />
                </div>
              ))}
              <div>
                <Button variant="outline" size="sm" onClick={() => setTemplateEnvs(prev => [...prev, 'new-environment'])} data-testid="template-add-env">
                  {t('orgEnvironments.templates.dialog.addEnvironment')}
                </Button>
              </div>
            </div>

            <div className="text-sm text-gray-600" data-testid="template-summary">
              {t('orgEnvironments.templates.dialog.willCreate')} {templateEnvs.length} {t('orgEnvironments.templates.dialog.envs')} / proyecto
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)} data-testid="template-cancel">
              {t('orgEnvironments.templates.dialog.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (selectedProjectIds.length === 0) {
                  toast.error(t('orgEnvironments.templates.dialog.atLeastOneProject'));
                  return;
                }
                if (templateEnvs.length === 0) {
                  toast.error(t('orgEnvironments.templates.dialog.atLeastOneEnvironment'));
                  return;
                }
                setTemplateDialogOpen(false);
                toast.info(t('orgEnvironments.templates.comingSoon'));
              }}
              data-testid="template-confirm"
            >
              {t('orgEnvironments.templates.dialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="border rounded-lg">
        {filteredEnvironments.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">{t('orgEnvironments.table.environment')}</TableHead>
                <TableHead className="w-[25%]">{t('orgEnvironments.table.project')}</TableHead>
                <TableHead className="w-[15%]">{t('orgEnvironments.table.status')}</TableHead>
                <TableHead className="w-[10%] text-right">{t('orgEnvironments.table.secrets')}</TableHead>
                <TableHead className="w-[10%] text-right">{t('orgEnvironments.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(
                filteredEnvironments.reduce((acc: Record<string, ApiEnvironment[]>, env: ApiEnvironment) => {
                  const key = env.projectName || t('orgEnvironments.unknownProject');
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(env);
                  return acc;
                }, {} as Record<string, ApiEnvironment[]>)
              ).map(([project, envs]) => (
                <React.Fragment key={`group-${project}`}>
                  <TableRow>
                    <TableCell colSpan={5} className="bg-gray-50 font-semibold text-gray-700">
                      {project}
                    </TableCell>
                  </TableRow>
                  {envs.map((environment: ApiEnvironment) => (
                    <TableRow key={environment.id} data-testid={`environment-row-${environment.slug || environment.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="p-1.5 bg-blue-100 rounded">
                            <GlobeIcon className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="font-medium truncate" title={environment.name}>{environment.name}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-[220px]" title={environment.projectName || t('orgEnvironments.unknownProject')}>
                        {environment.projectName || t('orgEnvironments.unknownProject')}
                      </TableCell>
                      <TableCell>
                        {environment.protected ? (
                          <Badge variant="destructive" className="inline-flex items-center gap-1">
                            <LockIcon className="w-3 h-3" />
                            {t('orgEnvironments.badges.protected')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="inline-flex items-center gap-1">
                            <UnlockIcon className="w-3 h-3" />
                            {t('orgEnvironments.badges.open')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-right">{environment.secretCount || 0}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`environment-actions-${environment.id}`}
                              title={t('common.actions')}
                            >
                              <MoreHorizontalIcon className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild data-testid={`environment-action-view-${environment.id}`}>
                              <Link
                                to="/org/$orgId/projects/$id/environments"
                                params={{ orgId, id: environment.projectId }}
                              >
                                {t('orgEnvironments.actions.viewDetails')}
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditEnvironment(environment)} data-testid={`environment-action-edit-${environment.id}`}>
                              <EditIcon className="w-4 h-4 mr-2" /> {t('orgEnvironments.actions.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSettings(environment)} data-testid={`environment-action-settings-${environment.id}`}>
                              <SettingsIcon className="w-4 h-4 mr-2" /> {t('orgEnvironments.actions.settings')}
                            </DropdownMenuItem>
                            {!environment.protected && (
                              <DropdownMenuItem
                                onClick={() => handleDeleteEnvironment(environment.id, environment.name, environment.projectId)}
                                className="text-red-600 focus:text-red-700"
                                data-testid={`environment-action-delete-${environment.id}`}
                              >
                                {t('orgEnvironments.actions.delete')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('orgEnvironments.empty.title')}</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery ? t('orgEnvironments.empty.searchHint') : t('orgEnvironments.empty.getStarted')}
              </p>
              {!searchQuery && (
                <Button onClick={() => setCreateEnvironmentOpen(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t('environments.createNew')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Environment Modal */}
      <CreateEnvironmentModal
        open={createEnvironmentOpen}
        onOpenChange={setCreateEnvironmentOpen}
        onSuccess={() => {
          refetch();
          setCreateEnvironmentOpen(false);
        }}
        organizationId={orgId}
      />
    </div>
  );
}

